import { inject, injectable } from 'tsyringe';
import { type ICompanyUserRepository } from 'application/repositorySchemas/ICompanyUserRepository';
import { type ICompanyUserTokenRepository } from 'application/repositorySchemas/ICompanyUserTokenRepository';
import { type ICompanyLoginByEmailClient } from 'interfaceAdapter/gatewaySchemas/ICompanyLoginByEmailClient';
import { type ICompanyUserLogoutClient } from 'interfaceAdapter/gatewaySchemas/ICompanyUserLogoutClient';
import {
    type ICompanyUserProfileRegistrationClient,
    IPostInput as IProfileRegistrationInput,
} from 'interfaceAdapter/gatewaySchemas/ICompanyUserProfileRegistrationClient';
import {
    type IEditCompanyUserProfileClient,
    IPostInput as IEditProfileInput,
} from 'interfaceAdapter/gatewaySchemas/IEditCompanyUserProfileClient';
import { type ICompanySendPasswordResetEmailClient } from 'interfaceAdapter/gatewaySchemas/ICompanySendPasswordResetEmailClient';
import { type ICompanyUserPasswordClient } from 'interfaceAdapter/gatewaySchemas/ICompanyUserPasswordClient';

@injectable()
export class CompanyUserRepository implements ICompanyUserRepository {
    public readonly token: string;

    constructor(
        @inject('ICompanyUserTokenRepository')
        public readonly companyUserToken: ICompanyUserTokenRepository,
        @inject('ICompanyLoginByEmailClient')
        public readonly companyLoginByEmail: ICompanyLoginByEmailClient,
        @inject('ICompanyUserLogoutClient')
        public readonly companyUserLogoutClient: ICompanyUserLogoutClient,
        @inject('ICompanyUserProfileRegistrationClient')
        public readonly companyUserProfileRegistration: ICompanyUserProfileRegistrationClient,
        @inject('IEditCompanyUserProfileClient')
        public readonly editCompanyUserProfile: IEditCompanyUserProfileClient,
        @inject('ICompanySendPasswordResetEmailClient')
        public readonly companySendPasswordResetEmailClient: ICompanySendPasswordResetEmailClient,
        @inject('ICompanyUserPasswordResetClient')
        public readonly companyUserPasswordResetClient: ICompanyUserPasswordClient,
    ) {
        this.token = this.companyUserToken.get() ?? '';
    }

    public loginByEmail = async (email: string, password: string) => {
        const { data, message } = await this.companyLoginByEmail.post({ email, password });
        if (message) {
            throw new Error(message);
        }
        this.companyUserToken.remove();
        this.companyUserToken.set(data.api_token);
        return data;
    };

    public logout = async () => {
        await this.companyUserLogoutClient.post({ token: this.token });
    };

    public registerProfile = async (company_user: IProfileRegistrationInput['company_user']) => {
        const { data, message } = await this.companyUserProfileRegistration.post({
            token: this.companyUserToken.get() || '',
            company_user,
        });

        if (message) {
            throw new Error(message);
        }

        return data;
    };

    public editProfile = async (company_user: IEditProfileInput['company_user']) => {
        const { data, message } = await this.editCompanyUserProfile.post({
            token: this.companyUserToken.get() || '',
            company_user,
        });

        if (message) {
            throw new Error(message);
        }

        return data;
    };

    public sendResetPasswordEmail = async (email: string) => {
        const { message } = await this.companySendPasswordResetEmailClient.post({ email });
        if (message) {
            throw new Error(message);
        }
    };

    public async resetPassword(code: string, password: string) {
        const { data, message } = await this.companyUserPasswordResetClient.post({
            code,
            password,
        });

        if (message) {
            throw new Error(message);
        }

        return { result: data };
    }
}
